import {createSelector} from 'reselect';
import {SOURCE_NETATMO_WEATHER} from '../../api';
import {
  NetatmoWeatherStation,
  NetatmoWeatherStationType,
  Utils,
} from '../../utils/NetatmoWeather';
import {CommonRootState} from '../types';
import {getSortedStationList} from '../weather/selectors';

const EMPTY_ARRAY: never[] = [];

export const selectNetatmoWeatherConnectedAt = (state: CommonRootState) =>
  state.netatmoWeather.link && state.netatmoWeather.link.date_connected;

export const selectNetatmoWeatherConnected = (state: CommonRootState) =>
  state.netatmoWeather.link !== null;

export const selectNetatmoWeatherActive = (state: CommonRootState) =>
  state.netatmoWeather.link !== null &&
  state.netatmoWeather.link.account.active;

export const getRawStationList = (state: CommonRootState) => {
  if (state.netatmoWeather.link !== null) {
    return state.netatmoWeather.link.account.extra_data.devices;
  }
  return EMPTY_ARRAY;
};

export const getUid = (_: CommonRootState, uid: string) => uid;

export const getGenericStationByUid = createSelector(
  getSortedStationList,
  getUid,
  (stationList, uid) => {
    return stationList.find(
      (s) => s.uid === uid && s.source === SOURCE_NETATMO_WEATHER,
    );
  },
);

export const getStationList = createSelector(
  getRawStationList,
  getSortedStationList,
  (stationList, genericStationList) =>
    Utils.getStationList(
      stationList as NetatmoWeatherStationType[],
      genericStationList,
    ),
);

export const getCurrentStation = createSelector(
  getStationList,
  (state: CommonRootState) => state.netatmoWeather.currentStation,
  (stationList, currentStationId) => {
    // Nullable on purpose: every branch below can leave these null (an account
    // with no station, or a LOGOUT that empties the slice under a mounted
    // screen — SMARTMIXIN-ANDROID-F1). The previous `null as
    // NetatmoWeatherStation` casts advertised a non-null result and let
    // `station.isPublic` ship. Note the workspace runs `strict: false`, so this
    // type documents the contract but does not enforce it: callers still have
    // to null-check by hand.
    let defaultStation: NetatmoWeatherStation | null = null;
    let station: NetatmoWeatherStation | null = null;
    if (stationList.length !== 0) {
      if (currentStationId !== null) {
        station = Utils.getStationFromId(stationList, currentStationId);
      }
      defaultStation = stationList[0];
    }
    return station || defaultStation;
  },
);

const getId = (_: CommonRootState, id: string) => id;

export const getStationByHomeId = createSelector(
  getStationList,
  getId,
  (stationList, id) => {
    const match = stationList.filter((station) => station.getHomeId() === id);
    if (match.length > 0) {
      return match[0];
    }
    return null;
  },
);

export const selectNetatmoWeatherChartConfig = (state: CommonRootState) =>
  state.netatmoWeather.chartConfig;
